import { formatMinutes } from '../lib/format';

const DAY_MS = 86_400_000;
const shortDate = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });

/** Daily active minutes as a bar chart (dates are UTC calendar days, as Google reports them). */
export function UsageChart({ usage, days = 30 }: { usage: { date: string; activeMinutes: number }[]; days?: number }) {
  const minutesByDate = new Map(usage.map((u) => [u.date.slice(0, 10), u.activeMinutes]));
  const now = new Date();
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const points = Array.from({ length: days }, (_, index) => {
    const date = new Date(today - (days - 1 - index) * DAY_MS);
    const key = date.toISOString().slice(0, 10);
    return { key, date, minutes: minutesByDate.get(key) ?? 0 };
  });

  const max = Math.max(60, ...points.map((p) => p.minutes));
  const niceMax = Math.ceil(max / 60) * 60;
  const width = 640;
  const height = 170;
  const left = 44;
  const bottom = 22;
  const top = 8;
  const chartWidth = width - left;
  const chartHeight = height - bottom - top;
  const slot = chartWidth / points.length;
  const barWidth = Math.max(2, slot - 3);
  const total = points.reduce((sum, p) => sum + p.minutes, 0);
  const activeDays = points.filter((p) => p.minutes > 0).length;

  return (
    <figure>
      <svg viewBox={`0 0 ${width} ${height}`} className="h-44 w-full" role="img" aria-label={`Daily active time for the last ${days} days`}>
        {[0, 0.5, 1].map((fraction) => {
          const y = top + chartHeight * (1 - fraction);
          return (
            <g key={fraction}>
              <line x1={left} x2={width} y1={y} y2={y} className="stroke-slate-200" strokeDasharray={fraction === 0 ? undefined : '3 3'} />
              <text x={left - 6} y={y + 3} textAnchor="end" className="fill-slate-400 text-[10px]">
                {formatMinutes(Math.round(niceMax * fraction))}
              </text>
            </g>
          );
        })}
        {points.map((point, index) => {
          const barHeight = point.minutes > 0 ? Math.max(2, (point.minutes / niceMax) * chartHeight) : 0;
          const x = left + index * slot + (slot - barWidth) / 2;
          const weekend = point.date.getUTCDay() === 0 || point.date.getUTCDay() === 6;
          return (
            <g key={point.key}>
              <rect x={left + index * slot} y={top} width={slot} height={chartHeight} className={weekend ? 'fill-slate-50' : 'fill-transparent'}>
                <title>{`${shortDate.format(point.date)}: ${formatMinutes(point.minutes)}`}</title>
              </rect>
              {barHeight > 0 && (
                <rect x={x} y={top + chartHeight - barHeight} width={barWidth} height={barHeight} rx={1.5} className="pointer-events-none fill-brand-500" />
              )}
              {(index % 7 === (points.length - 1) % 7 || index === points.length - 1) && (
                <text x={left + index * slot + slot / 2} y={height - 6} textAnchor="middle" className="fill-slate-400 text-[10px]">
                  {shortDate.format(point.date)}
                </text>
              )}
            </g>
          );
        })}
      </svg>
      <figcaption className="mt-1 text-xs text-slate-500">
        {formatMinutes(total)} of active use across {activeDays} day{activeDays === 1 ? '' : 's'} in the last {days} days
      </figcaption>
    </figure>
  );
}
